// Sign-in via txxt's redirect handoff (see TXXT-SSO-REQUEST-FROM-WEBP-LOVER.md).
// Phase 1 is display-only: the token is consumed from the redirect fragment,
// held in local storage, and never sent to the WebP Lover API.

const AUTH_KEY = 'webp-lover:txxt-auth:v1';
const FRAGMENT_PARAM = 'txxt_token';

export type TxxtAuth = {
  token: string;
  email: string;
  expires: number;
};

function decodeBase64Url(value: string): string | undefined {
  try {
    const padded = value.replaceAll('-', '+').replaceAll('_', '/');
    return atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  } catch {
    return undefined;
  }
}

export function parseTxxtToken(token: string): TxxtAuth | undefined {
  const [payload, signature] = token.split('.');
  if (!payload || !signature) return undefined;
  const decoded = decodeBase64Url(payload);
  if (!decoded) return undefined;
  const separator = decoded.lastIndexOf('|');
  if (separator <= 0) return undefined;
  const email = decoded.slice(0, separator).toLowerCase();
  const expires = Number(decoded.slice(separator + 1));
  if (!email.includes('@') || !Number.isFinite(expires)) return undefined;
  if (expires <= Date.now()) return undefined;
  return { token, email, expires };
}

export function loadStoredAuth(storage: Storage = localStorage): TxxtAuth | undefined {
  try {
    const value: unknown = JSON.parse(storage.getItem(AUTH_KEY) ?? 'null');
    if (!value || typeof value !== 'object') return undefined;
    const candidate = value as Partial<TxxtAuth>;
    if (typeof candidate.token !== 'string') return undefined;
    const parsed = parseTxxtToken(candidate.token);
    if (!parsed) {
      storage.removeItem(AUTH_KEY);
      return undefined;
    }
    return parsed;
  } catch {
    return undefined;
  }
}

export function storeAuth(auth: TxxtAuth, storage: Storage = localStorage): void {
  storage.setItem(AUTH_KEY, JSON.stringify(auth));
}

export function clearAuth(storage: Storage = localStorage): void {
  storage.removeItem(AUTH_KEY);
}

// Consumes `#txxt_token=...` after returning from the txxt handoff. The
// fragment is removed from the address bar immediately so the token is not
// left visible or copied with the URL.
export function consumeHandoffFragment(storage: Storage = localStorage): TxxtAuth | undefined {
  const hash = window.location.hash;
  if (!hash.startsWith(`#${FRAGMENT_PARAM}=`)) return undefined;
  const raw = decodeURIComponent(hash.slice(FRAGMENT_PARAM.length + 2));
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  const parsed = parseTxxtToken(raw);
  if (!parsed) return undefined;
  storeAuth(parsed, storage);
  return parsed;
}

export function handoffUrl(txxtOrigin: string, returnUrl: string): string {
  return `${txxtOrigin}/auth/handoff?return=${encodeURIComponent(returnUrl)}`;
}
